import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AccountType,
  getV1CompaniesCompanyIdAddOns,
  RutterItemizableType,
  RutterLedgerAccountType,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { createContext, ReactNode, useContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { AddOn } from '~hooks/useAddOns/models';
import { PaymentTerm } from '~hooks/usePaymentTerms/models';
import usePaymentTerms from '~hooks/usePaymentTerms/usePaymentTerms';
import {
  LineItemTypeOption,
  RutterConnection,
  RutterItem,
  RutterItemMapping,
  RutterLedgerAccount,
  RutterPaymentTerm,
} from '~hooks/useRutter/models';
import useRutter from '~hooks/useRutter/useRutter';
import { extractPagination, Pagination } from '~services/pagination';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { Nullable } from '~types/Nullable';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

const CONNECTION_ID_PARAM = 'connection_id';

// the two seeded core line item types every ticket line resolves to
const FREIGHT_LINE_ITEM_TYPE = 'FreightRateLineItem';
const MATERIAL_LINE_ITEM_TYPE = 'MaterialRateLineItem';
const CORE_LINE_ITEM_TYPE_NAMES = [FREIGHT_LINE_ITEM_TYPE, MATERIAL_LINE_ITEM_TYPE];

type PageFetcher<T> = (
  pagination: Pagination,
  signal?: AbortSignal,
) => Promise<{ data: T[]; pagination: Pagination } | null>;

// the mapping dropdowns need every QuickBooks object in memory (options list +
// reverse "which Tread row is this mapped to" lookup), so every dataset is walked
// to cursor exhaustion instead of being paginated in the UI
async function fetchAllPages<T>(fetchPage: PageFetcher<T>, signal?: AbortSignal) {
  const allRecords: T[] = [];
  let cursor: string | undefined;
  let hasMore = true;

  while (hasMore) {
    const response = await fetchPage(
      {
        limit: MAX_API_PAGE_SIZE,
        page: 1,
        ...(cursor ? { after: cursor } : {}),
      },
      signal,
    );

    // the useRutter getters toast and return null on a failure (a cancelled request is
    // rethrown instead); surface the failure to react-query so the page can render its error state
    if (!response) {
      throw new Error('failed to fetch a page');
    }

    allRecords.push(...response.data);
    cursor = response.pagination.after;
    hasMore = Boolean(cursor);
  }

  return allRecords;
}

interface QuickBooksMappingQueryContextValue {
  addOns: AddOn[];
  connection: Nullable<RutterConnection>;
  connectionId: Nullable<string>;
  coreLineItemTypes: LineItemTypeOption[];
  isError: boolean;
  isLoading: boolean;
  itemMappings: RutterItemMapping[];
  qboItems: RutterItem[];
  qboLedgerAccounts: RutterLedgerAccount[];
  qboPaymentTerms: RutterPaymentTerm[];
  refetchAddOns: () => Promise<unknown>;
  refetchPaymentTerms: () => Promise<unknown>;
  saveAccountMapping: (
    accountType: AccountType,
    accountId: string,
    currentQboRecordId: Nullable<string>,
    nextQboRecordId: Nullable<string>,
  ) => Promise<boolean>;
  saveItemMapping: (
    itemizableType: RutterItemizableType,
    itemizableId: string,
    nextQboItemId: Nullable<string>,
  ) => Promise<boolean>;
  saveLedgerMapping: (
    ledgerType: RutterLedgerAccountType,
    nextQboLedgerAccountId: Nullable<string>,
  ) => Promise<boolean>;
  savePaymentTermMapping: (
    paymentTermId: string,
    nextQboPaymentTermId: Nullable<string>,
  ) => Promise<boolean>;
  saveTaxRateMapping: (
    taxRateScheduleId: string,
    currentQboTaxRateId: Nullable<string>,
    nextQboTaxRateId: Nullable<string>,
  ) => Promise<boolean>;
  treadPaymentTerms: PaymentTerm[];
}

const QuickBooksMappingQueryContext =
  createContext<Nullable<QuickBooksMappingQueryContextValue>>(null);

export const quickBooksMappingConnectionQueryKey = (connectionId: Nullable<string>) => [
  'quickBooksMapping',
  'connection',
  connectionId,
];

export function useQuickBooksMappingQueries() {
  const context = useContext(QuickBooksMappingQueryContext);

  if (!context) {
    throw new Error(
      'useQuickBooksMappingQueries must be used within QuickBooksMappingQueryProvider',
    );
  }

  return context;
}

export function QuickBooksMappingQueryProvider({ children }: { children: ReactNode }) {
  const {
    fetchAllPaymentTerms,
    getConnections,
    getItemMappings,
    getItems,
    getLedgerAccounts,
    getLineItemTypes,
    updateCustomer,
    updateLedgerAccount,
    updatePaymentTerm,
    updateTaxRate,
    updateVendor,
    upsertItemMapping,
  } = useRutter();
  const { fetchAll: fetchAllTreadPaymentTerms } = usePaymentTerms();
  const { toasterStore, userStore } = useStores();
  const queryClient = useQueryClient();
  const location = useLocation();
  const companyId = getEffectiveUserCompanyId(userStore);

  const connectionId = useMemo(() => {
    return new URLSearchParams(location.search).get(CONNECTION_ID_PARAM);
  }, [location.search]);

  const connectionQuery = useQuery({
    queryKey: quickBooksMappingConnectionQueryKey(connectionId),
    enabled: Boolean(connectionId),
    queryFn: async ({ signal }) => {
      const response = await getConnections({ limit: 10, page: 1 }, signal);

      return response?.data.find((candidate) => candidate.id === connectionId) ?? null;
    },
  });

  const qboItemsQuery = useQuery({
    queryKey: ['quickBooksMapping', 'qboItems', connectionId],
    enabled: Boolean(connectionId),
    queryFn: ({ signal }) => {
      return fetchAllPages(
        (pagination, pageSignal) =>
          getItems(String(connectionId), pagination, pageSignal),
        signal,
      );
    },
  });

  const qboLedgerAccountsQuery = useQuery({
    queryKey: ['quickBooksMapping', 'qboLedgerAccounts', connectionId],
    enabled: Boolean(connectionId),
    queryFn: ({ signal }) => {
      return fetchAllPages((pagination, pageSignal) => {
        return getLedgerAccounts(String(connectionId), pagination, pageSignal);
      }, signal);
    },
  });

  const itemMappingsQuery = useQuery({
    queryKey: ['quickBooksMapping', 'itemMappings', connectionId],
    enabled: Boolean(connectionId),
    queryFn: async ({ signal }) => {
      const allMappings = await fetchAllPages((pagination, pageSignal) => {
        return getItemMappings(String(connectionId), pagination, pageSignal);
      }, signal);

      // a discarded mapping is a cleared one: keeping it would render its row as
      // mapped and make saveItemMapping treat a genuine re-save as a no-op
      return allMappings.filter((mapping) => !mapping.discardedAt);
    },
  });

  const qboPaymentTermsQuery = useQuery({
    queryKey: ['quickBooksMapping', 'qboPaymentTerms', connectionId],
    enabled: Boolean(connectionId),
    queryFn: async ({ signal }) => {
      const terms = await fetchAllPaymentTerms(String(connectionId), signal);

      // fetchAllPaymentTerms already toasts on a genuine failure and returns null the
      // same way it does for its deliberately quiet 403 (a user who can view the
      // settlement but not manage the accounting integration); throwing here would
      // conflate the quiet case with a real error and trip the page-wide isError banner
      return terms ?? [];
    },
  });

  // usePaymentTerms.fetchAll swallows a page failure and returns what it has, so this
  // query never reports isError and a failure renders a short list rather than the
  // page-wide banner. It still contributes its isLoading.
  const treadPaymentTermsQuery = useQuery({
    queryKey: ['quickBooksMapping', 'treadPaymentTerms', companyId],
    enabled: Boolean(companyId),
    queryFn: ({ signal }) => fetchAllTreadPaymentTerms(companyId, signal),
  });

  const addOnsQuery = useQuery({
    queryKey: ['quickBooksMapping', 'addOns', companyId],
    enabled: Boolean(companyId),
    queryFn: async ({ signal }) => {
      const allAddOns: AddOn[] = [];
      let cursor: string | undefined;
      let hasMore = true;

      while (hasMore) {
        const response = await getV1CompaniesCompanyIdAddOns({
          path: { 'company-id': companyId },
          query: {
            'page[limit]': MAX_API_PAGE_SIZE,
            ...(cursor ? { 'page[after]': cursor } : {}),
          },
          signal,
        });
        allAddOns.push(...response.data.data.map(AddOn.parse));
        cursor = extractPagination(response).after;
        hasMore = Boolean(cursor);
      }

      return allAddOns;
    },
  });

  const coreLineItemTypesQuery = useQuery({
    queryKey: ['quickBooksMapping', 'coreLineItemTypes', companyId],
    queryFn: async ({ signal }) => {
      const response = await getLineItemTypes(
        { pagination: { limit: MAX_API_PAGE_SIZE, page: 1 } },
        signal,
      );

      if (!response) {
        throw new Error('failed to fetch line item types');
      }

      // ordered Freight then Material, matching the design's Core lines section
      return CORE_LINE_ITEM_TYPE_NAMES.flatMap((coreName) => {
        return response.data.filter((lineItemType) => lineItemType.name === coreName);
      });
    },
  });

  const upsertQboLedgerAccountInCache = (updated: RutterLedgerAccount) => {
    queryClient.setQueryData<RutterLedgerAccount[]>(
      ['quickBooksMapping', 'qboLedgerAccounts', connectionId],
      (existing) => {
        return (existing ?? []).map((candidate) => {
          return candidate.id === updated.id ? updated : candidate;
        });
      },
    );
  };

  // the mapping FK lives on the QuickBooks-side record and is unique, so re-pointing a
  // Tread row is two sequential PATCHes: clear the old record first, then set the new
  // one. if the second call fails the row is left honestly unmapped (server truth), so the
  // caller reloads the page either way.
  const saveAccountMapping = async (
    accountType: AccountType,
    accountId: string,
    currentQboRecordId: Nullable<string>,
    nextQboRecordId: Nullable<string>,
  ) => {
    const isUnchanged = (currentQboRecordId ?? null) === (nextQboRecordId ?? null);
    if (isUnchanged) {
      return true;
    }

    const updateQboRecord =
      accountType === AccountType.CUSTOMER ? updateCustomer : updateVendor;

    if (currentQboRecordId) {
      const cleared = await updateQboRecord(currentQboRecordId, null);
      if (!cleared) {
        return false;
      }
    }

    if (nextQboRecordId) {
      const mapped = await updateQboRecord(nextQboRecordId, accountId);
      if (!mapped) {
        return false;
      }
    }

    return true;
  };

  const upsertQboPaymentTermInCache = (updated: RutterPaymentTerm) => {
    queryClient.setQueryData<RutterPaymentTerm[]>(
      ['quickBooksMapping', 'qboPaymentTerms', connectionId],
      (existing) => {
        return (existing ?? []).map((candidate) => {
          return candidate.id === updated.id ? updated : candidate;
        });
      },
    );
  };

  // same shape as saveAccountMapping: the mapping FK lives on the QuickBooks-side term
  // and is unique, so re-pointing a Tread term is clear-then-set
  const savePaymentTermMapping = async (
    paymentTermId: string,
    nextQboPaymentTermId: Nullable<string>,
  ) => {
    const currentQboTerm = (qboPaymentTermsQuery.data ?? []).find((qboTerm) => {
      return qboTerm.paymentTerm?.id === paymentTermId;
    });

    const isUnchanged = (currentQboTerm?.id ?? null) === (nextQboPaymentTermId ?? null);
    if (isUnchanged) {
      return true;
    }

    if (currentQboTerm) {
      const cleared = await updatePaymentTerm(currentQboTerm.id, null);
      if (!cleared) {
        return false;
      }
      upsertQboPaymentTermInCache(cleared);
    }

    if (nextQboPaymentTermId) {
      const mapped = await updatePaymentTerm(nextQboPaymentTermId, paymentTermId);
      if (!mapped) {
        await qboPaymentTermsQuery.refetch();

        return false;
      }
      upsertQboPaymentTermInCache(mapped);
    }

    return true;
  };

  const saveItemMapping = async (
    itemizableType: RutterItemizableType,
    itemizableId: string,
    nextQboItemId: Nullable<string>,
  ) => {
    const currentMapping = (itemMappingsQuery.data ?? []).find((mapping) => {
      return (
        mapping.rutterItemizable.type === itemizableType &&
        mapping.rutterItemizable.id === itemizableId
      );
    });

    const isUnchanged =
      (currentMapping?.rutterItemId ?? null) === (nextQboItemId ?? null);
    if (isUnchanged) {
      return true;
    }

    const savedMapping = await upsertItemMapping(
      String(connectionId),
      itemizableType,
      itemizableId,
      nextQboItemId ?? null,
    );
    if (!savedMapping) {
      await itemMappingsQuery.refetch();

      return false;
    }

    queryClient.setQueryData<RutterItemMapping[]>(
      ['quickBooksMapping', 'itemMappings', connectionId],
      (existing) => {
        const withoutRow = (existing ?? []).filter((mapping) => {
          return !(
            mapping.rutterItemizable.type === itemizableType &&
            mapping.rutterItemizable.id === itemizableId
          );
        });

        return savedMapping.discardedAt ? withoutRow : [...withoutRow, savedMapping];
      },
    );

    return true;
  };

  const saveLedgerMapping = async (
    ledgerType: RutterLedgerAccountType,
    nextQboLedgerAccountId: Nullable<string>,
  ) => {
    const currentQboLedgerAccount = (qboLedgerAccountsQuery.data ?? []).find(
      (ledgerAccount) => ledgerAccount.type === ledgerType,
    );

    const isUnchanged =
      (currentQboLedgerAccount?.id ?? null) === (nextQboLedgerAccountId ?? null);
    if (isUnchanged) {
      return true;
    }

    try {
      if (currentQboLedgerAccount) {
        const cleared = await updateLedgerAccount(currentQboLedgerAccount.id, null);
        upsertQboLedgerAccountInCache(cleared);
      }

      if (nextQboLedgerAccountId) {
        const mapped = await updateLedgerAccount(nextQboLedgerAccountId, ledgerType);
        upsertQboLedgerAccountInCache(mapped);
      }

      return true;
    } catch {
      toasterStore.push(
        alert(
          t('administration.integration.rutter.quick_books_online.grid.update_error'),
          AlertTypes.error,
        ),
      );
      await qboLedgerAccountsQuery.refetch();

      return false;
    }
  };

  // same shape as saveAccountMapping: the mapping FK lives on the QuickBooks-side rate
  // and is unique per connection, so re-pointing a Tread schedule is clear-then-set
  const saveTaxRateMapping = async (
    taxRateScheduleId: string,
    currentQboTaxRateId: Nullable<string>,
    nextQboTaxRateId: Nullable<string>,
  ) => {
    const isUnchanged = (currentQboTaxRateId ?? null) === (nextQboTaxRateId ?? null);
    if (isUnchanged) {
      return true;
    }

    if (currentQboTaxRateId) {
      const cleared = await updateTaxRate(currentQboTaxRateId, null);
      if (!cleared) {
        return false;
      }
    }

    if (nextQboTaxRateId) {
      const mapped = await updateTaxRate(nextQboTaxRateId, taxRateScheduleId);
      if (!mapped) {
        return false;
      }
    }

    return true;
  };

  const queries = [
    connectionQuery,
    qboItemsQuery,
    qboLedgerAccountsQuery,
    itemMappingsQuery,
    qboPaymentTermsQuery,
    treadPaymentTermsQuery,
    addOnsQuery,
    coreLineItemTypesQuery,
  ];

  const value: QuickBooksMappingQueryContextValue = {
    addOns: addOnsQuery.data ?? [],
    connection: connectionQuery.data ?? null,
    connectionId,
    coreLineItemTypes: coreLineItemTypesQuery.data ?? [],
    isError: queries.some((query) => query.isError),
    isLoading: queries.some((query) => query.isLoading),
    itemMappings: itemMappingsQuery.data ?? [],
    qboItems: qboItemsQuery.data ?? [],
    qboLedgerAccounts: qboLedgerAccountsQuery.data ?? [],
    qboPaymentTerms: qboPaymentTermsQuery.data ?? [],
    refetchAddOns: () => addOnsQuery.refetch(),
    refetchPaymentTerms: () => treadPaymentTermsQuery.refetch(),
    saveAccountMapping,
    saveItemMapping,
    saveLedgerMapping,
    savePaymentTermMapping,
    saveTaxRateMapping,
    treadPaymentTerms: treadPaymentTermsQuery.data ?? [],
  };

  return (
    <QuickBooksMappingQueryContext.Provider value={value}>
      {children}
    </QuickBooksMappingQueryContext.Provider>
  );
}
